/**
 * CR-6442: customer-view layout for the Purchasing → Deliveries filter panel.
 *
 * Regroups the existing filters into the five sections from the supplied
 * design — Date Range · Company · Supplier · Classification · Order Details —
 * without changing any control type or filter behaviour. The panel previously
 * used six differently-drawn groups (Quick Date Ranges, Company & Supplier,
 * Order, Commodity, Status, Dates), which split the date controls across the
 * top and bottom of the panel and mixed company with supplier filters.
 *
 * Section headings come from the app-wide `components/filters/FilterSection`
 * so this panel groups filters the same way as the dashboard, PO Lines and the
 * supplier-side Deliveries layout. Control density stays local to the
 * Deliveries panel (see deliveryFilterStyles) — these filters sit above a wide
 * data grid and are deliberately more compact than the dashboard's.
 *
 * The rework completes it. Company Country/Division, Supplier Country,
 * Strategic-only, Supplier type, Order type and Buyer code are here now, each
 * with a backing lookup on the GRN filterset — without one, django-filter
 * drops the param silently and the control would look right while doing
 * nothing, which is what the first pass was avoiding by leaving them out.
 *
 * Parent filters narrow their children (Country/Division → Site, Supplier
 * Country → Supplier Site, Commodity Group → Commodity Type) so the panel
 * cannot express a combination that is empty by construction.
 */
import { Calendar, Building2, Truck, Tags, FileText } from 'lucide-react';
import { ORIGINAL_REQUIRED_DATE } from '../../../../config/dateLabels';
import SearchableSelect from '../../../ui/SearchableSelect';
import { FilterSection } from '../../../filters/FilterSection';
import { countryDisplayNames } from '../../../dashboard/dashboardFilterUtils';
import { inputCls, labelCls, dateWrap, dateInputCls, gridCls } from './deliveryFilterStyles';
import { useFilterVisibility } from '../../../../hooks/useFilterVisibility';

/** Horizontal divider with padding so it doesn't touch card edges */
const SectionDivider = () => (
  <div className="mx-4 border-t border-border" />
);

interface FilterOption {
  value: string;
  label: string;
  /** On site options: drives Company → Country and its cascade. On supplier
   *  options: drives Supplier Country → Supplier Site. */
  country?: string;
  /** On site options: the division the site belongs to. On commodity types:
   *  the parent commodity group. Both drive parent → child narrowing. */
  group?: number | null;
}

export interface DeliveryFilterCustomerLayoutProps {
  filters: Record<string, string>;
  textInputs: Record<string, string>;
  pendingDates: { dateFrom: string; dateTo: string; oddFrom: string; oddTo: string };
  quickRanges: ReadonlyArray<{ key: string; label: string }>;
  getQuickRangeDates: (key: string) => { dateFrom: string; dateTo: string };
  viewScopedSites: FilterOption[];
  warehouseOptions: Array<{ id: number; name: string }>;
  supplierGroupOptions: FilterOption[];
  supplierOptions: FilterOption[];
  commodityGroupOptions: FilterOption[];
  commodityTypeOptions: FilterOption[];
  /**
   * CR-6442 rework: customer-side company groups for the Division dropdown.
   * CR-7472: already scoped to the current view and narrowed by the selected
   * Country by DeliveryFilterSection, so rendered as given.
   */
  divisionOptions?: FilterOption[];
  /** Country values present on the suppliers in scope. */
  supplierCountryOptions?: string[];
  onFilterChange: (key: string, value: string) => void;
}

export function DeliveryFilterCustomerLayout({
  filters,
  textInputs,
  pendingDates,
  quickRanges,
  getQuickRangeDates,
  viewScopedSites,
  warehouseOptions,
  supplierGroupOptions,
  supplierOptions,
  commodityGroupOptions,
  commodityTypeOptions,
  divisionOptions = [],
  supplierCountryOptions = [],
  onFilterChange,
}: DeliveryFilterCustomerLayoutProps) {
  // CR-4802: filters this tenant shows — one answer for every panel.
  const fv = useFilterVisibility();
  /** Country values on the sites this user can see, for Company → Country. */
  const customerCountryOptions = Array.from(
    new Set(viewScopedSites.map((s) => s.country).filter((c): c is string => !!c))
  ).sort((a, b) => (countryDisplayNames[a] || a).localeCompare(countryDisplayNames[b] || b));

  // CR-6442 rework: the dependent-filter behaviour the AC requires. Each
  // parent narrows what its child can offer, so the panel cannot express a
  // combination that returns nothing by construction. Clearing a now-invalid
  // child selection is handled where the filter state lives (DeliveryTab).
  const sitesForSelection = viewScopedSites.filter((s) => {
    if (filters['customerCountry'] && s.country !== filters['customerCountry']) return false;
    if (filters['division'] && String(s.group ?? '') !== filters['division']) return false;
    return true;
  });
  const suppliersForSelection = filters['supplierCountry']
    ? supplierOptions.filter((s) => s.country === filters['supplierCountry'])
    : supplierOptions;
  const commodityTypesForSelection = filters['commodityGroup']
    ? commodityTypeOptions.filter((t) => String(t.group ?? '') === filters['commodityGroup'])
    : commodityTypeOptions;

  /** Dropdown built from a plain string list (the country selects). */
  const countrySelect = (label: string, key: string, values: string[], allLabel: string) => (
    <div>
      <label className={labelCls}>{label}</label>
      <select
        value={filters[key] || ''}
        onChange={(e) => onFilterChange(key, e.target.value)}
        className={inputCls}
      >
        <option value="">{allLabel}</option>
        {values.map((c) => (
          <option key={c} value={c}>{countryDisplayNames[c] || c}</option>
        ))}
      </select>
    </div>
  );
  /** Free-text filter field — same input element and handler as before. */
  const textField = (label: string, key: string, value: string, placeholder: string) => (
    <div>
      <label className={labelCls}>{label}</label>
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onFilterChange(key, e.target.value)}
        className={inputCls}
      />
    </div>
  );

  /**
   * Date field. CR-6364: these bind to the pending (not-yet-applied) dates and
   * only take effect on Search, so picking From/To does not reload the grid on
   * every selection. That behaviour is unchanged here.
   */
  const dateField = (label: string, key: keyof typeof pendingDates) => (
    <div>
      <label className={labelCls}>{label}</label>
      <div className={dateWrap}>
        <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <input
          type="date"
          value={pendingDates[key]}
          onChange={(e) => onFilterChange(key, e.target.value)}
          className={dateInputCls}
        />
      </div>
    </div>
  );

  // CR-7106: one quick-range row per date pair — the same ranges now apply
  // to the ODD pair, which previously offered only raw date inputs.
  const quickRangeRow = (fromKey: 'dateFrom' | 'oddFrom', toKey: 'dateTo' | 'oddTo', rowLabel: string) => (
    <div className="flex flex-wrap items-center gap-2 mb-3">
      <span className="text-xs font-medium text-muted-foreground w-48">{rowLabel}</span>
      {quickRanges.map(({ key, label }) => {
        const dates = getQuickRangeDates(key);
        // CR-6364: reflect the pending (not-yet-applied) selection so a
        // clicked range highlights before it is applied.
        const isActive = pendingDates[fromKey] === dates.dateFrom && pendingDates[toKey] === dates.dateTo;
        return (
          <button
            key={key}
            onClick={() => {
              onFilterChange(fromKey, dates.dateFrom);
              onFilterChange(toKey, dates.dateTo);
            }}
            className={`px-3 py-1.5 text-xs font-medium border rounded-md transition-colors ${
              isActive
                ? 'border-primary bg-primary/10 text-primary hover:bg-primary/20'
                : 'border-border hover:border-primary hover:bg-primary/5 hover:text-primary'
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="[&>section]:border-t-0">
      {/* Divider above first section */}
      <SectionDivider />

      {/* Date Range — the design folds the quick ranges and both date pairs
          into one section; they were previously split top and bottom. */}
      <FilterSection title="Date Range" icon={Calendar}>
        {quickRangeRow('dateFrom', 'dateTo', 'Delivery Date:')}
        {quickRangeRow('oddFrom', 'oddTo', `${ORIGINAL_REQUIRED_DATE}:`)}
        <div className={gridCls}>
          {dateField('Delivery Date From', 'dateFrom')}
          {dateField('Delivery Date To', 'dateTo')}
          {dateField(`${ORIGINAL_REQUIRED_DATE} From`, 'oddFrom')}
          {dateField(`${ORIGINAL_REQUIRED_DATE} To`, 'oddTo')}
        </div>
      </FilterSection>

      <SectionDivider />

      {/* Company — customer-side hierarchy, split out of the old combined
          "Company & Supplier" group. */}
      <FilterSection title="Company" icon={Building2}>
        <div className={gridCls}>
          {fv.companyCountry && countrySelect('Country', 'customerCountry', customerCountryOptions, 'All countries')}
          {fv.division && (
          <div>
            <label className={labelCls}>Division</label>
            <SearchableSelect
              options={divisionOptions}
              value={filters['division'] || ''}
              onChange={(val) => onFilterChange('division', val)}
              placeholder="All divisions"
            />
          </div>
          )}
          <div>
            <label className={labelCls}>Company Site</label>
            <SearchableSelect
              options={sitesForSelection}
              value={filters['site'] || ''}
              onChange={(val) => onFilterChange('site', val)}
              placeholder="All Company Sites"
            />
          </div>
          {fv.warehouse && (
          <div>
            <label className={labelCls}>Warehouse</label>
            <select
              value={filters['warehouse'] || ''}
              onChange={(e) => onFilterChange('warehouse', e.target.value)}
              className={inputCls}
            >
              <option value="">All Warehouses</option>
              {warehouseOptions.map((wh) => (
                <option key={wh.id} value={String(wh.id)}>{wh.name}</option>
              ))}
            </select>
          </div>
          )}
        </div>
      </FilterSection>

      <SectionDivider />

      {/* Supplier — Supplier Group still narrows Supplier Site through the
          parent's existing onFilterChange cascade; that logic does not move. */}
      <FilterSection title="Supplier" icon={Truck}>
        <div className={gridCls}>
          {countrySelect('Country', 'supplierCountry', supplierCountryOptions, 'All countries')}
          <div>
            <label className={labelCls}>Supplier Group</label>
            <SearchableSelect
              options={supplierGroupOptions}
              value={filters['supplierGroup'] || ''}
              onChange={(val) => onFilterChange('supplierGroup', val)}
              placeholder="All Supplier Groups"
            />
          </div>
          <div>
            <label className={labelCls}>Supplier Site</label>
            <SearchableSelect
              options={suppliersForSelection}
              value={filters['supplier'] || ''}
              onChange={(val) => onFilterChange('supplier', val)}
              placeholder="All Supplier Sites"
            />
          </div>
          {fv.supplierCode && textField('Supplier Code', 'supplierCode', filters['supplierCode'] || '', 'Supplier code...')}
        </div>
        {/* Strategic-only sits under the section's fields on the design, as it
            does on the dashboard. Server-side it matches the (supplier,
            customer) pair, so it means "strategic to this customer". */}
        <label className="flex items-center gap-2 mt-3 text-xs font-medium cursor-pointer">
          <input
            type="checkbox"
            checked={filters['strategicOnly'] === 'true'}
            onChange={(e) => onFilterChange('strategicOnly', e.target.checked ? 'true' : '')}
            className="h-3.5 w-3.5 rounded border-border"
          />
          Strategic suppliers only
        </label>
      </FilterSection>

      {/* CR-4802: the whole group is tenant-gated; drop the heading when nothing is shown. */}
      {(fv.supplierType || fv.commodity) && (<>
        <SectionDivider />

        {/* Classification — the design's name for what was the Commodity group. */}
        <FilterSection title="Classification" icon={Tags}>
          <div className={gridCls}>
            {fv.supplierType && (
            <div>
              <label className={labelCls}>Supplier Type</label>
              <select
                value={filters['supplierType'] || ''}
                onChange={(e) => onFilterChange('supplierType', e.target.value)}
                className={inputCls}
              >
                <option value="">All suppliers</option>
                <option value="internal">Internal</option>
                <option value="external">External</option>
              </select>
            </div>
            )}
            {/* CR-7273: the Order Type control was removed. PurchaseOrder.order_type
                is dormant — no import path writes it, so every PO sits on the
                default and the filter could only ever return everything or
                nothing. The column stays; the UI only offers controls that work. */}
            {fv.commodity && (<>
            <div>
              <label className={labelCls}>Commodity Group</label>
              <SearchableSelect
                options={commodityGroupOptions}
                value={filters['commodityGroup'] || ''}
                onChange={(val) => onFilterChange('commodityGroup', val)}
                placeholder="All"
              />
            </div>
            <div>
              <label className={labelCls}>Commodity Type</label>
              <select
                value={filters['commodityType'] || ''}
                onChange={(e) => onFilterChange('commodityType', e.target.value)}
                className={inputCls}
              >
                <option value="">All</option>
                {commodityTypesForSelection.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            </>)}
          </div>
        </FilterSection>
      </>)}

      <SectionDivider />

      {/* Order Details — the design's fifth section merges what were the
          separate Order and Status groups. */}
      <FilterSection title="Order Details" icon={FileText}>
        <div className={gridCls}>
          {textField('PO Number', 'poNumber', textInputs['poNumber'] || '', 'PO Number...')}
          <div>
            <label className={labelCls}>PO Line Status</label>
            <select
              value={filters['poLineStatus'] || ''}
              onChange={(e) => onFilterChange('poLineStatus', e.target.value)}
              className={inputCls}
            >
              <option value="">All</option>
              <option value="open">Open</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          {textField('GRN Number', 'grnNumber', textInputs['grnNumber'] || '', 'GRN Number...')}
          {textField('Line Number', 'lineNumber', textInputs['lineNumber'] || '', 'Line number...')}
          {textField('Part Number', 'partNumber', textInputs['partNumber'] || '', 'Part number...')}
          {textField('Buyer Code', 'buyerCode', filters['buyerCode'] || '', 'Buyer code...')}
          <div>
            <label className={labelCls}>On-Time Status</label>
            <select
              value={filters['grnStatus'] || ''}
              onChange={(e) => onFilterChange('grnStatus', e.target.value)}
              className={inputCls}
            >
              <option value="">All</option>
              <option value="on_time">On Time</option>
              <option value="early">Early</option>
              <option value="late">Late</option>
              <option value="early_late">Early/Late</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Appeal Status</label>
            <select
              value={filters['appealFilter'] || ''}
              onChange={(e) => onFilterChange('appealFilter', e.target.value)}
              className={inputCls}
            >
              <option value="">All</option>
              <option value="no_appeal">Not Appealed</option>
              <option value="appealed">Appealed</option>
            </select>
          </div>
        </div>
      </FilterSection>
    </div>
  );
}
